import { mdiArrowCollapseUp, mdiChartTimelineVariant, mdiChevronDown, mdiChevronUp, mdiClose, mdiLinkVariant, mdiRobotOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { isDemoModeActive } from '../../../services/auth/demo-mode.utils';
import {
    draftFromRule,
    draftValidationError,
    IPaymentConditionDraft,
    IRuleDraft,
    orderWithDraft,
    ruleFromDraft,
} from '../../../services/denials/worker-rule-draft';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import { formatRuleMatchSummary } from '../../../services/worker-configuration/automation/worker-automation-scope.registry';
import {
    AutomationRule,
    AutomationWorkerId,
    MESSAGE_CATEGORIES,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    createEmptyRule,
    ENTER_TASK_FEE_DISCLAIMER,
    getDefaultActionValue,
    isRuleNameUniqueAcrossWorkers,
    normalizeRuleNameForStorage,
    RULE_ACTION_SELECT_LABELS,
    RULE_NAME_MAX_LENGTH,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { BaseButton, BaseInput, BaseSelect, ButtonGroup, IButtonGroupItemProps, ISelectOption } from '../../base';
import { TypeFilterChip } from '../../base/type-filter-bar/type-filter-bar';
import { ConfigurationAssignedUsers } from '../rules/configuration-assigned-users';
import { ConfigurationFieldLabel } from '../rules/configuration-action-summary';
import * as R from '../rules/configuration-rule.styles';
import * as S from './worker-rule-editor.styles';

/**
 * The worker rule editor (FE-116, Jordan's v3): create, edit and ORDER the rules of the denial or
 * rejection worker, from the denials page.
 *
 * ## What it does that the old dialog did not
 *
 * Opens an existing rule ("Edit worker"), shows the whole rule order with the draft in it, moves the
 * draft up and down, and warns when a rule above it also matches the row it was opened from — the
 * worker takes the first match, so such a draft would never fire for that row. Saving replaces the
 * worker's whole rule list, renumbered 1…n.
 *
 * ## What it deliberately does not
 *
 * - **Write functions.** An Execute Function rule shows its code, read-only; new ones are made on the
 *   Workers page, which has the function builder and its coder-rule picker (Vadim, 2026-10-02).
 * - **Edit payer, provider, exclusion or other conditions.** They are kept verbatim and listed.
 * - **Count claims a rule would hit.** The preview counts rows of THIS week's report the draft would
 *   cover, by the same estimate the table uses — there is no backtest over claims.
 *
 * Saving follows the old dialog's proven path: fetch the configuration fresh (the save replaces all of
 * it), drop defaults-only drafts, place the rule, save, and discard on failure so the Workers page
 * does not inherit a half-made draft.
 */

const GROUPS: Array<{ short: string; full: string }> = [
    { short: 'CO', full: 'CONTRACTUAL_OBLIGATIONS' },
    { short: 'OA', full: 'OTHER_ADJUSTMENTS' },
    { short: 'PI', full: 'PAYER_INITIATED_REDUCTIONS' },
    { short: 'PR', full: 'PATIENT_RESPONSIBILITY' },
    { short: 'CR', full: 'CORRECTIONS_AND_REVERSALS' },
];
const SHORT_BY_FULL = Object.fromEntries(GROUPS.map(group => [group.full, group.short]));

const OWNER_OPTIONS: IButtonGroupItemProps[] = [{ label: 'ENTER' }, { label: 'PROVIDER' }];

const FUNCTION_HERE =
    'Functions are written on the Workers page, which has the function builder and the coder-rule picker. This editor shows a function, it does not write one.';

const WORKER_TITLE: Record<AutomationWorkerId, { title: string; key: string }> = {
    denial: { title: 'Denial Worker', key: 'PROVIDER_DENIAL_WORKER' },
    rejection: { title: 'Rejection Worker', key: 'PROVIDER_REJECTION_WORKER' },
};

export type SourceMatch = 'full' | 'conditional' | null;

export interface IWorkerRuleEditorProps {
    store: WorkerStore;
    kind: AutomationWorkerId;
    /** What the editor opens with: a saved rule's draft, or a new one seeded from a row. */
    initial: IRuleDraft;
    /** `OA-23` / `“Code 26 · Entity not found”` — the row the editor was opened from. */
    sourceLabel: string;
    /** Whether a rule matches that row: drives the "runs first" warnings. */
    matchesSource: (rule: AutomationRule) => SourceMatch;
    /** "Matches 1 combination · 125 adjustments in this week's report". */
    preview: (rule: AutomationRule) => string;
    /** Rules that already name the row but miss it — offered as "Edit that rule" on a new draft. */
    related?: Array<{ rule: AutomationRule; why: string }>;
    /** A short code taken from the row, offered as the broader phrase: `Code 21`. */
    phraseSuggestion?: string;
    onClose: () => void;
    onSaved: (rule: AutomationRule) => void;
}

const newRuleId = (): string => createEmptyRule('PAYMENT').id;

/** Comma or Enter adds what was typed; Backspace on an empty box removes the last chip. */
function ChipInput({
    values,
    onChange,
    placeholder,
    normalize,
    disabled,
    label,
}: {
    values: string[];
    onChange: (values: string[]) => void;
    placeholder: string;
    normalize: (raw: string) => string;
    disabled?: boolean;
    label: string;
}): React.ReactElement {
    const [text, setText] = React.useState('');
    const add = (raw: string): void => {
        const value = normalize(raw);
        if (value && !values.includes(value)) {
            onChange([...values, value]);
        }
        setText('');
    };

    return (
        <S.ChipField>
            {values.map(value => (
                <S.Chip key={value}>
                    {value}
                    <BaseButton
                        variant="text"
                        size="small"
                        type="button"
                        aria-label={`Remove ${value}`}
                        disabled={disabled}
                        onClick={(): void => onChange(values.filter(v => v !== value))}>
                        <Icon path={mdiClose} size={0.55} />
                    </BaseButton>
                </S.Chip>
            ))}
            <BaseInput
                compact
                value={text}
                aria-label={label}
                placeholder={values.length ? '' : placeholder}
                disabled={disabled}
                disableLatin1Validation={true}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setText(event.target.value)}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                    if (event.key === 'Enter' || event.key === ',') {
                        event.preventDefault();
                        add(text);
                    } else if (event.key === 'Backspace' && !text && values.length) {
                        onChange(values.slice(0, -1));
                    }
                }}
                onBlur={(): void => add(text)}
            />
        </S.ChipField>
    );
}

export const WorkerRuleEditor: React.FC<IWorkerRuleEditorProps> = observer(
    ({ store, kind, initial, sourceLabel, matchesSource, preview, related = [], phraseSuggestion, onClose, onSaved }) => {
        const demo = isDemoModeActive();
        const [draft, setDraft] = React.useState<IRuleDraft>(initial);
        const baseRules = store.draftAutomationWorkers[kind]?.rules || [];

        // Where the draft sits: before the rule shown after it, or last. Editing starts where the rule is.
        const initialBefore = (): string | null => {
            if (!initial.editId) {
                return null;
            }
            const index = baseRules.findIndex(rule => rule.id === initial.editId);
            return index >= 0 && index + 1 < baseRules.length ? baseRules[index + 1].id : null;
        };
        const [beforeId, setBeforeId] = React.useState<string | null>(initialBefore);
        const [query, setQuery] = React.useState('');
        const [saving, setSaving] = React.useState(false);
        const [error, setError] = React.useState<string | null>(null);

        React.useEffect(() => {
            if (!store.configuration && !store.loading) {
                void store.fetchConfiguration();
            }
        }, [store]);

        const draftRule = ruleFromDraft(draft, () => '__draft__');
        const ordered = orderWithDraft(baseRules, draftRule, beforeId);
        const position = ordered.findIndex(rule => rule.id === draftRule.id);
        const draftHits = matchesSource(draftRule);
        const update = (patch: Partial<IRuleDraft>): void => {
            setDraft(current => ({ ...current, ...patch }));
            setError(null);
        };

        const editRule = (rule: AutomationRule): void => {
            setDraft(draftFromRule(kind, rule));
            const index = baseRules.findIndex(candidate => candidate.id === rule.id);
            setBeforeId(index >= 0 && index + 1 < baseRules.length ? baseRules[index + 1].id : null);
            setError(null);
        };
        const moveTo = (index: number): void => {
            // `index` is where the draft should end up in `ordered`; anchor it on the rule that will follow.
            const others = ordered.filter(rule => rule.id !== draftRule.id);
            const clamped = Math.max(0, Math.min(others.length, index));
            setBeforeId(clamped < others.length ? others[clamped].id : null);
        };

        const conflicts = draftHits
            ? ordered
                  .slice(0, position)
                  .map((rule, index) => ({ rule, index, hit: matchesSource(rule) }))
                  .filter(entry => entry.hit)
            : [];

        let saveBlocked: string | null = null;
        if (demo) {
            saveBlocked = 'Saving is switched off in demo mode.';
        } else if (store.readOnly) {
            saveBlocked = 'This console can view worker configuration but not change it.';
        } else if (store.dirty && !store.hasRecommendedDefaultUpdatesOnly) {
            saveBlocked = 'The Workers page has unsaved changes. Save or discard them there first.';
        }
        const validation = draftValidationError(draft);

        const save = async (): Promise<void> => {
            if (saveBlocked || validation || saving) {
                setError(validation);
                return;
            }
            setSaving(true);
            setError(null);
            await store.fetchConfiguration();
            if (store.error) {
                setError(store.error);
                setSaving(false);
                return;
            }
            if (store.hasRecommendedDefaultUpdatesOnly) {
                store.discardChanges();
            }
            const name = normalizeRuleNameForStorage(draft.name);
            if (!isRuleNameUniqueAcrossWorkers(store.draftAutomationWorkers, name, draft.editId)) {
                setError(`A rule named “${name}” already exists. Rule names are unique across both workers.`);
                setSaving(false);
                return;
            }
            const rule = ruleFromDraft({ ...draft, name }, newRuleId);
            const fresh = store.draftAutomationWorkers[kind]?.rules || [];
            store.replaceAutomationRules(kind, orderWithDraft(fresh, rule, beforeId));
            const saved = await store.saveConfiguration();
            if (!saved) {
                const message = store.saveError || store.automationRuleValidationError || 'The configuration was not saved.';
                store.discardChanges();
                setError(message);
                setSaving(false);
                return;
            }
            setSaving(false);
            onSaved(rule);
        };

        const ruleSummary = (rule: AutomationRule): string =>
            formatRuleMatchSummary(rule.match, store.catalog, store.payerById, store.providerById, store.facilityById) ||
            'No conditions';

        const visible = (rule: AutomationRule): boolean => {
            const needle = query.trim().toLowerCase();
            return !needle || `${rule.name} ${ruleSummary(rule)}`.toLowerCase().includes(needle);
        };

        const setCondition = (index: number, patch: Partial<IPaymentConditionDraft>): void =>
            update({ payment: draft.payment.map((condition, i) => (i === index ? { ...condition, ...patch } : condition)) });

        const actionType = draft.action.type;
        const actionValue = draft.action.value || {};
        const setActionField = (field: string, value: unknown): void =>
            update({ action: { type: actionType, value: { ...actionValue, [field]: value } } });
        const chooseAction = (type: RuleActionType): void => {
            if (type !== actionType) {
                update({ action: { type, value: getDefaultActionValue(type) } });
            }
        };
        const offered: RuleActionType[] = ['CREATE_MESSAGE', 'EXECUTE_ENTER_FUNCTION', 'SWITCH_TO_PRIVATE_PAY'];
        // A saved rule may use an action the design does not list (Close Out, Reconsideration): keep it on offer.
        const actions = offered.includes(actionType) ? offered : [...offered, actionType];
        const categoryOptions: ISelectOption<string>[] = [
            ...new Set([...MESSAGE_CATEGORIES, String(actionValue.messageCategory || '')].filter(Boolean)),
        ].map(value => ({ label: value, value }));

        const title = WORKER_TITLE[kind];

        return (
            <S.Editor>
                <S.Header>
                    <Icon path={mdiRobotOutline} size={1} />
                    <S.HeaderText>
                        <S.Title>
                            {title.title}
                            <S.WorkerKey>{title.key}</S.WorkerKey>
                        </S.Title>
                        <S.Sub>
                            {draft.editId ? 'Editing' : 'New rule'} at priority {position + 1} of {ordered.length}
                            {sourceLabel ? ` · opened from ${sourceLabel}` : ''}
                        </S.Sub>
                    </S.HeaderText>
                    <BaseButton variant="text" size="small" aria-label="Close" onClick={onClose}>
                        <Icon path={mdiClose} size={0.8} />
                    </BaseButton>
                </S.Header>

                <S.Body>
                    <S.OrderColumn>
                        <S.OrderHead>
                            <S.OrderTitle>
                                Rule order<span>{ordered.length} rules</span>
                            </S.OrderTitle>
                            <S.OrderHint>Evaluated top to bottom &mdash; the first match wins</S.OrderHint>
                            <BaseInput
                                compact
                                value={query}
                                placeholder="Find a rule by name or code"
                                aria-label="Find a rule by name or code"
                                disableLatin1Validation={true}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => setQuery(event.target.value)}
                            />
                        </S.OrderHead>
                        <S.OrderList>
                            {ordered.map((rule, index) => {
                                const isDraft = rule.id === draftRule.id;
                                if (!isDraft && !visible(rule)) {
                                    return null;
                                }
                                const hit = isDraft ? null : matchesSource(rule);
                                const above = index < position;
                                const blocking = Boolean(hit && draftHits && above);
                                const name = isDraft ? draft.name.trim() || 'Untitled rule' : rule.name;
                                return (
                                    <S.OrderCard
                                        key={rule.id}
                                        draft={isDraft}
                                        blocking={blocking}
                                        title={isDraft ? undefined : 'Open this rule'}
                                        onClick={(): void => {
                                            if (!isDraft && !saving) {
                                                editRule(rule);
                                            }
                                        }}>
                                        <S.OrderNumber>{index + 1}</S.OrderNumber>
                                        <S.OrderText>
                                            <S.OrderName draft={isDraft}>{name}</S.OrderName>
                                            <S.OrderSummary>{ruleSummary(isDraft ? draftRule : rule)}</S.OrderSummary>
                                            <S.OrderSummary>
                                                {RULE_ACTION_SELECT_LABELS[rule.action.type] || rule.action.type}
                                            </S.OrderSummary>
                                            {isDraft && sourceLabel && (
                                                <S.OrderFlag tone={draftHits ? 'primary' : 'warning'}>
                                                    {draftHits ? `Matches ${sourceLabel}` : `Doesn’t match ${sourceLabel}`}
                                                </S.OrderFlag>
                                            )}
                                            {!isDraft && hit && draftHits && (
                                                <S.OrderFlag tone={above ? 'attention' : 'warning'}>
                                                    {above
                                                        ? hit === 'conditional'
                                                            ? 'Also matches for some payers · runs first'
                                                            : 'Also matches · runs first'
                                                        : 'Also matches · your rule runs first'}
                                                </S.OrderFlag>
                                            )}
                                        </S.OrderText>
                                        {isDraft && (
                                            <S.OrderMove>
                                                <BaseButton
                                                    variant="text"
                                                    size="small"
                                                    aria-label="Move up"
                                                    disabled={position === 0 || saving}
                                                    onClick={(): void => moveTo(position - 1)}>
                                                    <Icon path={mdiChevronUp} size={0.7} />
                                                </BaseButton>
                                                <BaseButton
                                                    variant="text"
                                                    size="small"
                                                    aria-label="Move down"
                                                    disabled={position >= ordered.length - 1 || saving}
                                                    onClick={(): void => moveTo(position + 1)}>
                                                    <Icon path={mdiChevronDown} size={0.7} />
                                                </BaseButton>
                                            </S.OrderMove>
                                        )}
                                    </S.OrderCard>
                                );
                            })}
                        </S.OrderList>
                    </S.OrderColumn>

                    <S.FormColumn>
                        {conflicts.map(({ rule, index, hit }) => (
                            <S.Banner tone="attention" key={rule.id}>
                                <Icon path={mdiArrowCollapseUp} size={0.8} />
                                <span>
                                    &ldquo;{rule.name}&rdquo; (priority {index + 1}) also matches {sourceLabel}
                                    {hit === 'conditional'
                                        ? ' for some payers and runs first for them.'
                                        : ' and runs first — this rule will never fire for it.'}
                                </span>
                                <BaseButton variant="outlined" size="compact" disabled={saving} onClick={(): void => moveTo(index)}>
                                    Move above it
                                </BaseButton>
                            </S.Banner>
                        ))}
                        {!draft.editId &&
                            related.map(({ rule, why }) => (
                                <S.Banner tone="warning" key={rule.id}>
                                    <Icon path={mdiLinkVariant} size={0.8} />
                                    <span>
                                        <strong>{rule.name}</strong> &middot; {why}
                                    </span>
                                    <BaseButton variant="outlined" size="compact" disabled={saving} onClick={(): void => editRule(rule)}>
                                        Edit that rule
                                    </BaseButton>
                                </S.Banner>
                            ))}

                        <S.Field>
                            <S.Label htmlFor="worker-rule-name">Rule name</S.Label>
                            <BaseInput
                                id="worker-rule-name"
                                value={draft.name}
                                maxLength={RULE_NAME_MAX_LENGTH}
                                disabled={saving}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                    update({ name: event.target.value.slice(0, RULE_NAME_MAX_LENGTH) })
                                }
                            />
                            <S.Hint>Tasks this rule creates are categorized under this name.</S.Hint>
                        </S.Field>

                        <S.StepHead>
                            <S.StepTag>WHEN</S.StepTag>
                            {kind === 'denial'
                                ? 'Payment — ERA adjustment codes'
                                : 'Transmission status of type Rejected'}
                        </S.StepHead>

                        {kind === 'denial' ? (
                            draft.payment.map((condition, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && <S.Joiner>AND</S.Joiner>}
                                    <S.Block>
                                        <S.Field>
                                            <S.Label>
                                                CARC group<span>empty = any group</span>
                                            </S.Label>
                                            <S.Toggles>
                                                {GROUPS.map(group => {
                                                    const on = condition.groups.includes(group.full);
                                                    return (
                                                        <TypeFilterChip
                                                            key={group.full}
                                                            active={on}
                                                            disabled={saving}
                                                            onClick={(): void =>
                                                                setCondition(index, {
                                                                    groups: on
                                                                        ? condition.groups.filter(g => g !== group.full)
                                                                        : [...condition.groups, group.full],
                                                                })
                                                            }>
                                                            {group.short}
                                                        </TypeFilterChip>
                                                    );
                                                })}
                                            </S.Toggles>
                                        </S.Field>
                                        <S.TwoColumns>
                                            <S.Field>
                                                <S.Label>
                                                    CARC code<span>empty = any</span>
                                                </S.Label>
                                                <ChipInput
                                                    label="CARC code"
                                                    values={condition.carc}
                                                    placeholder="e.g. 97"
                                                    disabled={saving}
                                                    normalize={(raw): string =>
                                                        raw.trim().toUpperCase().replace(/^(CO|OA|PI|PR|CR)[-:]?/, '')
                                                    }
                                                    onChange={(values): void => setCondition(index, { carc: values })}
                                                />
                                            </S.Field>
                                            <S.Field>
                                                <S.Label>
                                                    RARC code<span>all of these · an alternative to the CARC</span>
                                                </S.Label>
                                                <ChipInput
                                                    label="RARC code"
                                                    values={condition.rarc}
                                                    placeholder="e.g. N19"
                                                    disabled={saving}
                                                    normalize={(raw): string => raw.trim().toUpperCase()}
                                                    onChange={(values): void => setCondition(index, { rarc: values })}
                                                />
                                            </S.Field>
                                        </S.TwoColumns>
                                        <S.Hint>
                                            Matches a payment carrying the CARC (in that group), OR carrying every RARC listed.
                                            To require both, add a second condition group.
                                        </S.Hint>
                                        {draft.payment.length > 1 && (
                                            <BaseButton
                                                variant="text"
                                                size="small"
                                                disabled={saving}
                                                onClick={(): void => update({ payment: draft.payment.filter((_, i) => i !== index) })}>
                                                Remove this condition group
                                            </BaseButton>
                                        )}
                                    </S.Block>
                                </React.Fragment>
                            ))
                        ) : (
                            <S.Block>
                                <S.Field>
                                    <S.Label>
                                        Transmission status contains<span>any of these (OR)</span>
                                    </S.Label>
                                    <ChipInput
                                        label="Phrase the message contains"
                                        values={draft.contains}
                                        placeholder="Type a phrase or code and press Enter…"
                                        disabled={saving}
                                        normalize={(raw): string => raw.trim()}
                                        onChange={(values): void => update({ contains: values })}
                                    />
                                </S.Field>
                                {phraseSuggestion && !draft.contains.includes(phraseSuggestion) && (
                                    <S.Hint>
                                        Match on any part of the message.{' '}
                                        <BaseButton
                                            variant="text"
                                            size="small"
                                            disabled={saving}
                                            onClick={(): void => update({ contains: [...draft.contains, phraseSuggestion] })}>
                                            Add &ldquo;{phraseSuggestion}&rdquo;
                                        </BaseButton>{' '}
                                        and the worker picks up every rejection containing it &mdash; or keep the entire
                                        message to match only that one.
                                    </S.Hint>
                                )}
                            </S.Block>
                        )}
                        {kind === 'denial' && (
                            <BaseButton
                                variant="text"
                                size="small"
                                disabled={saving}
                                onClick={(): void => update({ payment: [...draft.payment, { groups: [], carc: [], rarc: [] }] })}>
                                + Add a condition group (AND)
                            </BaseButton>
                        )}
                        {draft.kept.length > 0 && (
                            <S.Kept>
                                <strong>Kept from this rule, unchanged:</strong>{' '}
                                {formatRuleMatchSummary(draft.kept, store.catalog, store.payerById, store.providerById, store.facilityById)}
                                . Change these on the Workers page.
                            </S.Kept>
                        )}

                        <S.StepHead>
                            <S.StepTag then>THEN</S.StepTag>
                            action
                        </S.StepHead>
                        <S.Block>
                            <S.Toggles>
                                {actions.map(type => {
                                    // A function can be KEPT on a rule that has one, never chosen here.
                                    const locked = type === 'EXECUTE_ENTER_FUNCTION' && actionType !== type;
                                    return (
                                        <S.Slot key={type} title={locked ? FUNCTION_HERE : undefined}>
                                            <TypeFilterChip
                                                active={actionType === type}
                                                disabled={saving || locked}
                                                onClick={(): void => chooseAction(type)}>
                                                {RULE_ACTION_SELECT_LABELS[type] || type}
                                            </TypeFilterChip>
                                        </S.Slot>
                                    );
                                })}
                            </S.Toggles>

                            {actionType === 'CREATE_MESSAGE' && (
                                <>
                                    <S.TwoColumns>
                                        <S.Field>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageOwner}>
                                                Message owner
                                            </ConfigurationFieldLabel>
                                            <ButtonGroup
                                                variant="pills"
                                                buttons={OWNER_OPTIONS}
                                                selected={actionValue.messageOwner === 'PROVIDER' ? OWNER_OPTIONS[1] : OWNER_OPTIONS[0]}
                                                onSelect={(item): void => setActionField('messageOwner', item.label)}
                                            />
                                        </S.Field>
                                        <S.Field>
                                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageCategory}>
                                                Category
                                            </ConfigurationFieldLabel>
                                            <BaseSelect
                                                options={categoryOptions}
                                                value={categoryOptions.find(o => o.value === (actionValue.messageCategory || 'PAYER_FOLLOWUP'))}
                                                disabled={saving}
                                                onChange={(selected: unknown): void => {
                                                    const option = selected as ISelectOption<string> | null;
                                                    if (option?.value) {
                                                        setActionField('messageCategory', option.value);
                                                    }
                                                }}
                                            />
                                        </S.Field>
                                    </S.TwoColumns>
                                    <S.Field>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.assigned}>Assign to</ConfigurationFieldLabel>
                                        <ConfigurationAssignedUsers
                                            disabled={saving}
                                            loading={store.assignableUsersLoading}
                                            assignedBankUserIds={(actionValue.assigned as string[] | undefined) ?? []}
                                            users={store.assignableUsers}
                                            onChange={(ids): void => setActionField('assigned', ids)}
                                        />
                                        {!((actionValue.assigned as string[] | undefined) ?? []).length && (
                                            <S.Hint>Unassigned &mdash; lands in the message owner&apos;s queue.</S.Hint>
                                        )}
                                    </S.Field>
                                    <S.Field>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.suggestedResolution}>
                                            Suggested resolution
                                        </ConfigurationFieldLabel>
                                        <R.FieldTextarea
                                            rows={3}
                                            placeholder="What should the assignee do?"
                                            disabled={saving}
                                            value={String(actionValue.suggestedResolution ?? '')}
                                            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                                                setActionField('suggestedResolution', event.target.value)
                                            }
                                        />
                                        {actionValue.messageOwner === 'ENTER' && <S.Hint>{ENTER_TASK_FEE_DISCLAIMER}</S.Hint>}
                                    </S.Field>
                                </>
                            )}

                            {actionType === 'EXECUTE_ENTER_FUNCTION' && (
                                <S.Field>
                                    <S.Label>Function · edits the claim payload before resubmission</S.Label>
                                    <S.Code>{String(actionValue.function || '') || '// no code'}</S.Code>
                                    <S.Hint>{FUNCTION_HERE}</S.Hint>
                                </S.Field>
                            )}

                            {actionType === 'SWITCH_TO_PRIVATE_PAY' && (
                                <S.Field>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.memo} optional>
                                        Invoice memo
                                    </ConfigurationFieldLabel>
                                    <R.FieldTextarea
                                        rows={2}
                                        placeholder="Optional note on the claim"
                                        disabled={saving}
                                        value={String(actionValue.memo ?? '')}
                                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                                            setActionField('memo', event.target.value)
                                        }
                                    />
                                    <S.Hint>Moves the balance to patient responsibility and stops billing the payer.</S.Hint>
                                </S.Field>
                            )}

                            {!offered.includes(actionType) && (
                                <S.Hint>
                                    This rule&apos;s action ({RULE_ACTION_SELECT_LABELS[actionType] || actionType}) is kept as
                                    saved. Change its settings on the Workers page.
                                </S.Hint>
                            )}
                        </S.Block>
                    </S.FormColumn>
                </S.Body>

                <S.Footer>
                    <S.Preview>
                        <Icon path={mdiChartTimelineVariant} size={0.8} />
                        {preview(draftRule)}
                    </S.Preview>
                    {(error || saveBlocked) && <S.ErrorText role="alert">{error || saveBlocked}</S.ErrorText>}
                    <BaseButton variant="outlined" size="compact" disabled={saving} onClick={onClose}>
                        Cancel
                    </BaseButton>
                    <S.Slot title={saveBlocked || validation || undefined}>
                        <BaseButton
                            variant="primary"
                            size="compact"
                            disabled={Boolean(saveBlocked) || Boolean(validation) || saving || store.loading}
                            onClick={(): void => void save()}>
                            {saving ? 'Saving…' : draft.editId ? 'Save rule' : 'Add rule'}
                        </BaseButton>
                    </S.Slot>
                </S.Footer>
            </S.Editor>
        );
    },
);

/** For the page: the short group code a rule stores in full. */
export const shortGroup = (full: string): string => SHORT_BY_FULL[full] || full;
